import { Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { firstValueFrom, map, Observable, of, Subject, tap } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { Job, JobStatus } from '../models/job.model';
import { Apollo } from 'apollo-angular';
import { CREATE_JOB, UPDATE_JOB, UPDATE_ORDER_DELIVERY_DETAILS } from '../queries/job-query';
import { Timestamp } from '@angular/fire/firestore';
import { SentryService } from './sentry.service';

@Injectable({
  providedIn: 'root',
})
export class JobService {
  private appVisibilityChange = new Subject<void>();
  // A public Observable that other components can subscribe to.
  appVisibilityChange$ = this.appVisibilityChange.asObservable();
  constructor(
    private afs: AngularFirestore,
    private afAuth: AngularFireAuth,
    public apollo: Apollo,
    public sentry: SentryService
  ) {}

  notifyAppVisibilityChange() {
    this.appVisibilityChange.next();
  }

  getPotentialJobByRiderId(riderId: string): Observable<any | null> {
    return this.afs
      .collection(environment.RIDER_JOBS)
      .valueChanges()
      .pipe(
        map((jobs) => {
          const now = Timestamp.now();
          const nowMillis = now.seconds * 1000 + now.nanoseconds / 1e6;
          const threshold = nowMillis - 24 * 60 * 60 * 1000; // 24 hours ago
          return (
            jobs
              .filter((job: any) =>
                job.potentialRiders?.some(
                  (rider: any) =>
                    rider.riderId === riderId && rider.status === 'NOTIFIED'
                )
              )
              .filter((job: any) => {
                const jobTimestamp =
                  job.timings.orderPlacedAt.seconds * 1000 +
                  job.timings.orderPlacedAt.nanoseconds / 1e6;
                return jobTimestamp >= threshold;
              })
              .sort((a: any, b: any) => {
                const aMillis =
                  a.timings.orderPlacedAt.seconds * 1000 +
                  a.timings.orderPlacedAt.nanoseconds / 1e6;

                const bMillis =
                  b.timings.orderPlacedAt.seconds * 1000 +
                  b.timings.orderPlacedAt.nanoseconds / 1e6;

                return aMillis - bMillis; // ascending (oldest first)
              })
          );
        })
      );
  }

  getAssignedJobByRiderId(riderId: string): Observable<any | null> {
    if (riderId) {
      return this.afs
        .collection(environment.RIDER_JOBS, (ref) =>
          ref
            .where('assignedRiderId', '==', riderId)
            .where('jobStatus', 'in', ['ACCEPTED', 'PICKED_UP_BY_AGENT'])
        )
        .valueChanges({ idField: 'id' })
        .pipe(
          map((jobs) => {
            const now = Timestamp.now();
            const nowMillis = now.seconds * 1000 + now.nanoseconds / 1e6;
            const threshold = nowMillis - 24 * 60 * 60 * 1000; // 24 hours ago

            const recentJobs = jobs.filter((job: any) => {
              const jobTimestamp =
                job.timings.orderPlacedAt.seconds * 1000 +
                job.timings.orderPlacedAt.nanoseconds / 1e6;
              return jobTimestamp >= threshold;
            });

            return recentJobs.length > 0 ? recentJobs[0] : null;
          })
        );
    } else return of(null);
  }

  async updateJobStatus(orderId: string, jobStatus: string): Promise<void> {
    if (!orderId || !jobStatus) return;
  
    try {
      const snapshot = await firstValueFrom(
        this.afs
          .collection(environment.RIDER_JOBS, (ref) =>
            ref.where('orderId', '==', orderId)
          )
          .get()
      );
  
      if (snapshot.empty) {
        console.error('Job not found!');
        return;
      }
  
      const now = Timestamp.now();
  
      const updatePromises = snapshot.docs.map(async (doc) => {
        const updateData: any = { jobStatus };
  
        if (jobStatus === 'PICKED_UP_BY_AGENT') {
          updateData['timings.orderPickedUpAt'] = now;
        } else if (jobStatus === 'DELIVERED') {
          updateData['timings.orderDeliveredAt'] = now;
        }
  
        try {
          await this.afs
            .collection(environment.RIDER_JOBS)
            .doc(doc.id)
            .update(updateData);
          console.log(`Status and timing updated successfully for doc: ${doc.id}`);
        } catch (error) {
          console.error(`Error updating doc: ${doc.id}`, error);
        }
      });
  
      await Promise.all(updatePromises);
    } catch (error) {
      this.sentry.logError(error,{
        step: "update job status",
        orderId: orderId,
        jobStatus: jobStatus
      })
      console.error('Error fetching jobs:', error);
    }
  }
  

  async updateJobId(orderId: string, backendId: string): Promise<void> {
    if (!orderId || !backendId) return Promise.resolve();

    try {
      const snapshot = await firstValueFrom(
        this.afs
          .collection(environment.RIDER_JOBS, (ref) =>
            ref.where('orderId', '==', orderId)
          )
          .get()
      );

      if (snapshot.empty) {
        console.error('Job not found!');
        return;
      }

      const batch = this.afs.firestore.batch();
      snapshot.docs.forEach((doc) => {
        const docRef = this.afs
          .collection(environment.RIDER_JOBS)
          .doc(doc.id).ref;
        batch.update(docRef, { backendId });
      });

      await batch.commit();
      console.log(
        `Status updated successfully for ${snapshot.docs.length} documents`
      );
      this.sentry.logMessage("Rider accepted job - updateJobId ",'info',{
        orderId: orderId,
        backendId: backendId
      })
    } catch (error) {
      this.sentry.logError(error,{
        step: "Rider accepted job - updateJobId",
        orderId: orderId,
        backendId: backendId
      });
      console.error('Error updating jobs:', error);
    }
  }

  async updateJobStatusWithPotentialRider(
    orderId: string,
    riderId: string
  ): Promise<void> {
    try {
      const querySnapshot = await this.afs
        .collection(environment.RIDER_JOBS, (ref) =>
          ref.where('orderId', '==', orderId)
        )
        .get()
        .toPromise();

      if (querySnapshot?.empty) {
        throw new Error('Order not found');
      }

      const doc = querySnapshot?.docs[0];
      const docId = doc?.id;
      const jobData = doc?.data() as Job;

      if (!jobData) return;

      jobData.jobStatus = JobStatus.ACCEPTED;
      jobData.assignedRiderId = riderId;
      jobData.timings.orderUpdatedAt = Timestamp.now();

      jobData.potentialRiders = jobData.potentialRiders.map((rider: any) => {
        if (rider.riderId === riderId) {
          return { ...rider, status: 'ACCEPTED' };
        }
        return rider;
      });

      await this.afs
        .collection(environment.RIDER_JOBS)
        .doc(docId)
        .update(jobData);
      console.log('Job updated successfully');
      this.sentry.logMessage("Rider accepted job - updateJobStatusWithPotentialRider",'info',{
        jobData: jobData
      })
    } catch (error) {
      console.error('Error updating job status:', error);
      this.sentry.logError(error,{
        step: "Rider accepted job - updateJobStatusWithPotentialRider",
        orderId: orderId,
        riderId: riderId
      });
      throw error;
    }
  }

  async declinePotentialRider(orderId: string, riderId: string): Promise<void> {
    try {
      const querySnapshot = await this.afs
        .collection(environment.RIDER_JOBS, (ref) =>
          ref.where('orderId', '==', orderId)
        )
        .get()
        .toPromise();

      if (querySnapshot?.empty) {
        throw new Error('Order not found');
      }

      const doc = querySnapshot?.docs[0];
      const docId = doc?.id;
      const jobData = doc?.data() as Job;

      if (!jobData) return;

      jobData.potentialRiders = jobData.potentialRiders.map((rider: any) => {
        if (rider.riderId === riderId) {
          return { ...rider, status: JobStatus.REJECTED };
        }
        return rider;
      });

      await this.afs
        .collection(environment.RIDER_JOBS)
        .doc(docId)
        .update(jobData);
      console.log('Job updated successfully');
      this.sentry.logMessage("Rider declined job",'info',{
        jobData: jobData
      })
    } catch (error) {
      console.error('Error updating job status:', error);
      this.sentry.logError(error,{
        step: "Rider declined job",
        orderId: orderId,
        riderId: riderId
      });
      throw error;
    }
  }

  async createJobBackend(job: Job): Promise<any> {
    const today = new Date().toISOString().split('T')[0];
    const jobInput = {
      id: null,
      orderId: job.orderId,
      jobDate: today,
      assignedRiderId: job.assignedRiderId,
      origin: {
        name: job.origin.name,
        location: job.origin.location,
      },
      destination: {
        name: job.destination.name,
        location: job.destination.location,
      },
      searchParams: job.searchParams,
      jobStatus: JobStatus.ACCEPTED,
      deliveryCharge: {
        baseFee: job.deliveryCharge.baseFee,
        tax: job.deliveryCharge.tax,
        total: job.deliveryCharge.total,
      },
      tipAmount: job.tipAmount,
    };

    try {
      const result = await firstValueFrom(
        this.apollo.mutate({
          mutation: CREATE_JOB,
          variables: { job: jobInput },
        })
      );
      return result.data;
    } catch (error) {
      console.error('Error creating job:', error);
      this.sentry.logError(error,{
        step: "creating job in backend",
        job: jobInput
      })
      throw error;
    }
  }

  async updateJobBackend(updateJobId: string | null, job: any): Promise<any> {
    try {
      const result = await firstValueFrom(
        this.apollo.mutate({
          mutation: UPDATE_JOB,
          variables: {
            updateJobId,
            job,
          },
        })
      );
      return result.data;
    } catch (error) {
      console.error('Error updating job:', error);
      this.sentry.logError(error,{
        step: "update job in backend",
        updateJobId: updateJobId,
        job: job,
      })
      throw error;
    }
  }

  async updateOrderDeliveryDetails(
    orderId: string,
    businessId: string,
    deliveryDetails: any
  ) {
    try {
      const result = await firstValueFrom(
        this.apollo.mutate({
          mutation: UPDATE_ORDER_DELIVERY_DETAILS,
          variables: {
            orderId,
            businessId,
            deliveryDetails,
          },
        })
      );
      return result.data;
    } catch (error) {
      console.error('Error updating order:', error);
      this.sentry.logError(error,{
        step: "update order delivery details",
        orderId: orderId,
        businessId: businessId,
        deliveryDetails: deliveryDetails,
      })
      throw error;
    }
  }
}
